/**
 * FloorPlanLayoutSection — native lift of the legacy `FloorPlanLayout` (2D only):
 * room-names, dimensions, scalebar, compass, LOGO card (now on the file hub),
 * disclaimer, format, file-orientation, multi-story, output language.
 *
 * The logo card renders a `FileAPIUpload` bound to the floor-plan-logo file-hub
 * kind (§4b) — the legacy `FloorPlanLogoUpload` (which rode the legacy visual
 * store) is replaced entirely (closes the LAST non-FileAPI upload).
 */

import React, { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_900 } from 'constants/styling/theme'
import { FileType } from 'constants/misc'
import { BinaryButtonSwitch } from 'components/common/BinaryButtonSwitch'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { Label } from 'components/common/Label'
import { MUICheckCard } from 'components/common/MUICards'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { MUIInputField } from 'components/common/MUIInputField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import { SystemMessage } from 'components/common/SystemMessage'
import type { ToggleFilterValue } from 'components/common/ToggleFilter'
import type { FileAPIEntry } from 'components/common/FileAPI'
import { Language } from 'translations/Language'

import type { FloorPlanConfigDraftDTO } from '../floorPlanConfig.dto'
import { FloorPlanFileOrientation, FloorPlanFormat } from 'constants/floorPlan'
import { FloorPlanToggleSetting } from 'components/common/FloorPlanControls'

/** JPEG only, as in legacy (`acceptedProductDocumentMimetypes`). */
const LOGO_FILE_TYPES = [FileType.JPG, FileType.JPEG]

/** 10 MB, product's limit for the logo (QA-485). */
const LOGO_MAX_BYTES = 10 * 1024 * 1024
export const isLogoSizeAllowed = (size: number): boolean => size > 0 && size <= LOGO_MAX_BYTES

interface Props {
  value: FloorPlanConfigDraftDTO
  patch: (partial: Partial<FloorPlanConfigDraftDTO>) => void
  isEditing: boolean
  logoEntries: FileAPIEntry[]
  uploadLogo: (files: File[]) => void
  deleteLogo: (id: string) => void
}

/**
 * @component FloorPlanLayoutSection
 * 2D layout options + the file-hub logo zone.
 */
export const FloorPlanLayoutSection: React.FC<Props> = ({ value, patch, isEditing, logoEntries, uploadLogo, deleteLogo }) => {
  const { t } = useTranslation(['purchase_flow', 'order', 'step_address', 'enums', 'language', 'upload_files'])

  const dimensions = value.dimensions ?? { showDimensionChain: false, showEachRoomArea: false }
  const outputFileFormat = value.outputFileFormat ?? []
  const fileOrientation = value.fileOrientation ?? []

  const formatToggleValues: ToggleFilterValue<FloorPlanFormat>[] = useMemo(
    () => [FloorPlanFormat.JPG, FloorPlanFormat.PDF].map((format) => ({ value: format, displayName: format })),
    []
  )

  const toggleFormat = (format: FloorPlanFormat) => {
    const next = outputFileFormat.includes(format)
      ? outputFileFormat.filter((f) => f !== format)
      : [...outputFileFormat, format]
    patch({ outputFileFormat: next })
  }

  const toggleFileOrientation = (orientation: FloorPlanFileOrientation, select: boolean) => {
    const set = new Set(fileOrientation)
    if (select) set.add(orientation)
    else set.delete(orientation)
    patch({ fileOrientation: Array.from(set) })
  }

  return (
    <SectionedBorderBox title={t('floor_plan_step.layout.title')}>
      <Box display="flex" flexDirection="column" gap={1.6}>

        <MUICheckCard
          checked={value.roomNames}
          onCheck={() => patch({ roomNames: !value.roomNames })}
          expandableContent={<SystemMessage message={t(isEditing ? 'floor_plan_step.layout.add_room_name_message' : 'floor_plan_step.layout.add_room_name_message_capture')} />}
        >
          <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.layout.add_room_name')}
          </Typography>
        </MUICheckCard>

        <MUICheckCard
          checked={!!value.isActiveDimensions}
          onCheck={() => {
            const nextActive = !value.isActiveDimensions
            // Legacy gated dimensions at serialize (`isActiveDimensions ? raw : false`) and never
            // cleared the children on toggle-off. Native has no serialize step, so clear the children
            // here — else the wire DTO ships {isActiveDimensions:false, dimensions:{chain:true}} and the
            // ordered plan renders dimension chains the user disabled (verify-fpconfig MUST-FIX).
            patch(nextActive
              ? { isActiveDimensions: true }
              : { isActiveDimensions: false, dimensions: { showDimensionChain: false, showEachRoomArea: false } })
          }}
          expandableContent={(
            <Stack gap=".8rem">

              <FloorPlanToggleSetting
                title={t('floor_plan_step.layout.showcase_dimension_chains')}
                value={dimensions.showDimensionChain}
                onChange={(v) => patch({ dimensions: { ...dimensions, showDimensionChain: v } })}
              />

              {dimensions.showDimensionChain &&
                <SystemMessage message={t('floor_plan_step.layout.dimension_chains_message')} />
              }

              <FloorPlanToggleSetting
                title={t('floor_plan_step.layout.showcase_room_area')}
                value={dimensions.showEachRoomArea}
                onChange={(v) => patch({ dimensions: { ...dimensions, showEachRoomArea: v } })}
              />

            </Stack>
          )}
        >
          <Stack gap=".6rem" flexDirection="row" alignItems="center">
            <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
              {t('floor_plan_step.layout.add_dimensions')}
            </Typography>
          </Stack>
        </MUICheckCard>

        <MUICheckCard
          checked={value.scaleBar}
          onCheck={() => patch({ scaleBar: !value.scaleBar })}
        >
          <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.layout.add_scalebar')}
          </Typography>
        </MUICheckCard>

        <MUICheckCard
          checked={value.compass}
          onCheck={() => patch({ compass: !value.compass })}
          expandableContent={<SystemMessage message={t('floor_plan_step.layout.compass_info_message')} />}
        >
          <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.layout.add_compass')}
          </Typography>
        </MUICheckCard>

        <MUICheckCard
          checked={!!value.isActiveLogo}
          onCheck={() => patch({ isActiveLogo: !value.isActiveLogo })}
          expandableContent={
            <FileAPIUpload
              title={t('floor_plan_step.layout.add_company_logo')}
              files={logoEntries}
              maxNumberOfFiles={1}
              isDraggingFile={false}
              acceptedFileTypes={LOGO_FILE_TYPES}
              onCheckFileSize={isLogoSizeAllowed}
              fileSizeCheckFailedWarning={t('upload_files:file_size_check_failed_with_limits', { minSize: '0 MB', maxSize: '10 MB' })}
              uploadHandler={(acceptedFiles) => uploadLogo(acceptedFiles)}
              onDelete={(fileId) => deleteLogo(fileId)}
              onCancelUpload={(fileId) => deleteLogo(fileId)}
            />
          }
        >
          <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.layout.add_company_logo')}
          </Typography>
        </MUICheckCard>

        <MUICheckCard
          checked={!!value.showDisclaimer}
          onCheck={() => {
            const nextShow = !value.showDisclaimer
            // Legacy gated disclaimer at serialize (`showDisclaimer ? text : undefined`); clear the text
            // on toggle-off so a disabled disclaimer never ships + prints (verify-fpconfig MUST-FIX).
            patch(nextShow ? { showDisclaimer: true } : { showDisclaimer: false, disclaimer: '' })
          }}
          expandableContent={
            <Box>
              <MUIInputField
                isMultiline
                maxLength={200}
                value={value.disclaimer ?? ''}
                placeholder={t('floor_plan_step.layout.disclaimer_placeholder')}
                onChange={(e) => patch({ disclaimer: e.target.value })}
                isError={!!value.showDisclaimer && !value.disclaimer}
                errorText={t('floor_plan_step.layout.disclaimer_error')}
              />
            </Box>
          }
        >
          <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
            {t('floor_plan_step.layout.disclaimer')}
          </Typography>
        </MUICheckCard>

        <SectionedBorderBox title={t('floor_plan_step.layout.format')}>
          <Stack gap={1}>

            <Stack gap={0.5}>

              <Label text={t('floor_plan_step.layout.export')} required />

              <SelectableButtonsGrid
                showCheckbox
                preventUnselect
                activeButtonClickable
                values={formatToggleValues}
                selectedValue={outputFileFormat as FloorPlanFormat[]}
                onSelect={(format) => toggleFormat(format)}
                onUnselect={(format) => toggleFormat(format)}
              />

            </Stack>

            <Stack gap={0.5}>

              <Label text={t('floor_plan_step.layout.orientation')} required />

              <SelectableButtonsGrid
                preventUnselect
                activeButtonClickable
                showCheckbox
                values={Object.values(FloorPlanFileOrientation).map((orientation) => ({
                  value: orientation,
                  displayName: t(`enums:floor_plan_orientation.${orientation}`),
                }))}
                selectedValue={fileOrientation}
                onSelect={(orientation) => toggleFileOrientation(orientation, true)}
                onUnselect={(orientation) => toggleFileOrientation(orientation, false)}
              />

            </Stack>

          </Stack>
        </SectionedBorderBox>

        <SectionedBorderBox title={t('floor_plan_step.layout.multi_story_title')}>
          <Stack gap={0.5}>

            <Label text={t('floor_plan_step.layout.multi_story_label')} required />

            <BinaryButtonSwitch
              value={!!value.isMultiStoryPlan}
              preventUnselect
              onChange={(isMultiStory) => patch({ isMultiStoryPlan: isMultiStory ?? false })}
            />

          </Stack>
        </SectionedBorderBox>

        <SectionedBorderBox title={t('floor_plan_step.layout.output_lang')}>
          <Box width="50%">
            <MUIDropdown
              button={(isOpen, action) => (
                <MUIDropdownInput
                  readOnly
                  type={DropdownInputTypes.SELECTOR}
                  value={t(`language:${value.outputFileLanguage.toUpperCase()}`, value.outputFileLanguage)}
                  onClick={action}
                />
              )}
            >
              {Object.values(Language).map((lang) => (
                <MUIDropdownItem key={lang} onClick={() => patch({ outputFileLanguage: lang })}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                    {t(`language:${lang.toUpperCase()}`, lang)}
                  </Typography>
                </MUIDropdownItem>
              ))}
            </MUIDropdown>
          </Box>
        </SectionedBorderBox>

      </Box>
    </SectionedBorderBox>
  )
}
